import { useCallback, useEffect, useState } from 'react';

const STORAGE_KEY = 'sync-theme';
const mq = () => window.matchMedia('(prefers-color-scheme: dark)');

function systemTheme() {
  return mq().matches ? 'dark' : 'light';
}

function storedTheme() {
  try {
    const t = localStorage.getItem(STORAGE_KEY);
    return t === 'light' || t === 'dark' ? t : null;
  } catch {
    return null;
  }
}

/**
 * Theme follows Chrome / the OS by default (prefers-color-scheme).
 * Clicking the toggle sets a manual override, persisted in localStorage.
 * If the override matches the system preference we clear it, so the
 * site keeps tracking the OS again — seamless either way.
 */
export default function useTheme() {
  const [theme, setTheme] = useState(() => storedTheme() || systemTheme());

  // Keep <html data-theme> in sync (only when overriding the system).
  useEffect(() => {
    const root = document.documentElement;
    const override = storedTheme();
    if (override) root.setAttribute('data-theme', override);
    else root.removeAttribute('data-theme');
  }, [theme]);

  // Track OS changes while no manual override is set.
  useEffect(() => {
    const media = mq();
    const onChange = () => {
      if (!storedTheme()) setTheme(systemTheme());
    };
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, []);

  const toggle = useCallback(() => {
    const next = theme === 'dark' ? 'light' : 'dark';
    try {
      if (next === systemTheme()) localStorage.removeItem(STORAGE_KEY);
      else localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* storage unavailable — theme still flips for this session */
    }
    setTheme(next);
  }, [theme]);

  return { theme, toggle };
}
